import React, { useCallback } from 'react';
import { useTooltipEvents } from './useTooltipEvents';
import { TooltipContent } from './TooltipContent';
import { zIndexValue as zIndexPosition } from '../helpers/position';
import { useDeviceType, useDidUpdateEffect, useWindowEventListener } from '../hooks';
import { isDefined } from '../helpers/defined';
/*
 Helper which returns parent ref, portal node to render and modified events
 */

export var useTooltipPortal = function useTooltipPortal(_ref) {
  var open = _ref.open,
      _ref$defaultOpen = _ref.defaultOpen,
      defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
      _ref$withOverflowHidd = _ref.withOverflowHidden,
      withOverflowHidden = _ref$withOverflowHidd === void 0 ? false : _ref$withOverflowHidd,
      _ref$animate = _ref.animate,
      animate = _ref$animate === void 0 ? true : _ref$animate,
      parentQuerySelector = _ref.parentQuerySelector,
      parentElement = _ref.parentElement,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 8 : _ref$offset,
      _ref$multiline = _ref.multiline,
      multiline = _ref$multiline === void 0 ? false : _ref$multiline,
      _ref$trigger = _ref.trigger,
      triggerProp = _ref$trigger === void 0 ? 'hover' : _ref$trigger,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? 'top' : _ref$position,
      onClick = _ref.onClick,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose,
      onToggle = _ref.onToggle,
      onOpened = _ref.onOpened,
      onClosed = _ref.onClosed,
      onToggled = _ref.onToggled,
      content = _ref.content,
      renderContent = _ref.renderContent,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? zIndexPosition('tooltip') : _ref$zIndex,
      _ref$noPadding = _ref.noPadding,
      noPadding = _ref$noPadding === void 0 ? false : _ref$noPadding,
      _ref$withMargin = _ref.withMargin,
      withMargin = _ref$withMargin === void 0 ? false : _ref$withMargin,
      wrapperStyle = _ref.wrapperStyle,
      customWrapper = _ref.customWrapper,
      width = _ref.width,
      height = _ref.height,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'small' : _ref$size,
      _ref$appearType = _ref.appearType,
      appearType = _ref$appearType === void 0 ? 'slide' : _ref$appearType,
      _ref$inverse = _ref.inverse,
      inverse = _ref$inverse === void 0 ? false : _ref$inverse,
      _ref$blurred = _ref.blurred,
      blurred = _ref$blurred === void 0 ? false : _ref$blurred,
      _ref$noFlip = _ref.noFlip,
      noFlip = _ref$noFlip === void 0 ? true : _ref$noFlip,
      onBeforeOutsideChange = _ref.onBeforeOutsideChange,
      onBeforeInsideChange = _ref.onBeforeInsideChange,
      excludeRef = _ref.excludeRef,
      _ref$closeOnScroll = _ref.closeOnScroll,
      closeOnScroll = _ref$closeOnScroll === void 0 ? false : _ref$closeOnScroll,
      _ref$closeOnResize = _ref.closeOnResize,
      closeOnResize = _ref$closeOnResize === void 0 ? false : _ref$closeOnResize,
      _ref$closeOnOutsideCl = _ref.closeOnOutsideClick,
      closeOnOutsideClick = _ref$closeOnOutsideCl === void 0 ? true : _ref$closeOnOutsideCl,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$ready = _ref.ready,
      ready = _ref$ready === void 0 ? true : _ref$ready,
      onReady = _ref.onReady,
      closeDelay = _ref.closeDelay,
      _ref$component = _ref.component,
      TooltipComponent = _ref$component === void 0 ? TooltipContent : _ref$component;

  var _useDeviceType = useDeviceType(),
      hasTouchScreen = _useDeviceType.hasTouchScreen;

  var trigger = triggerProp === null ? null : hasTouchScreen ? 'click' : triggerProp;

  var _useTooltipEvents = useTooltipEvents({
    open: open,
    defaultOpen: defaultOpen,
    trigger: trigger,
    onOpen: onOpen,
    onClose: onClose,
    onToggle: onToggle,
    onOpened: onOpened,
    onClosed: onClosed,
    onToggled: onToggled,
    onClick: onClick,
    onBeforeOutsideChange: onBeforeOutsideChange,
    onBeforeInsideChange: onBeforeInsideChange,
    closeOnOutsideClick: closeOnOutsideClick,
    excludeRef: excludeRef,
    onReady: onReady,
    ready: ready,
    closeDelay: closeDelay
  }),
      opened = _useTooltipEvents.opened,
      openedDebounced = _useTooltipEvents.openedDebounced,
      parentRef = _useTooltipEvents.parentRef,
      childRef = _useTooltipEvents.childRef,
      onMouseClick = _useTooltipEvents.onMouseClick,
      onMouseOver = _useTooltipEvents.onMouseOver,
      onMouseOut = _useTooltipEvents.onMouseOut,
      onChildMouseOver = _useTooltipEvents.onChildMouseOver,
      onChildMouseOut = _useTooltipEvents.onChildMouseOut,
      setOpened = _useTooltipEvents.setOpened;

  useDidUpdateEffect(function () {
    if (isDefined(open) && open !== opened) {
      setOpened(open);
    }
  }, [open]);
  var portalNode = (opened || animate && openedDebounced) && /*#__PURE__*/React.createElement(TooltipComponent, {
    noFlip: noFlip,
    inverse: inverse,
    blurred: blurred,
    size: size,
    appearType: appearType,
    position: position,
    offset: offset,
    multiline: multiline,
    noPadding: noPadding,
    withMargin: withMargin,
    withOverflowHidden: withOverflowHidden,
    animate: animate,
    width: width,
    height: height,
    parentRef: parentRef,
    childRef: childRef,
    zIndex: zIndex,
    trigger: trigger,
    content: renderContent ? renderContent({
      opened: opened,
      openedDebounced: openedDebounced
    }) : content,
    show: opened,
    wrapperStyle: wrapperStyle,
    customWrapper: customWrapper,
    parentElement: parentElement,
    parentQuerySelector: parentQuerySelector
  });
  var setClose = useCallback(function () {
    return setOpened(false);
  }, []);
  var setOpen = useCallback(function () {
    return setOpened(true);
  }, []);
  useWindowEventListener('scroll', setClose, !opened || !closeOnScroll, true);
  useWindowEventListener('resize', setClose, !opened || !closeOnResize);
  useDidUpdateEffect(function () {
    if (disabled) {
      setClose();
    }
  }, [disabled]);
  return {
    ref: parentRef,
    childRef: childRef,
    onMouseClick: onMouseClick,
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut,
    onChildMouseOver: onChildMouseOver,
    onChildMouseOut: onChildMouseOut,
    portalNode: portalNode,
    opened: opened,
    openedDebounced: openedDebounced,
    setClose: setClose,
    setOpen: setOpen
  };
};